import {
    useAuth0
} from '@auth0/auth0-react';
import {
    Box,
    useTheme
} from '@mui/material';
import {
    type FC,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import {
    toast
} from 'react-toastify';

import Section from '../Section';

import type {
    OwnershipRequest
} from '@views/OrganizationListingPage/types';

import Button from '@components/typed/Button';
import ButtonBlock from '@components/typed/ButtonBlock';
import InlineLoader from '@components/typed/InlineLoader';
import Modal from '@components/typed/Modal';
import Typography from '@components/typed/Typography';

import customerDataRequest from '@services/api/customer-data';

import {
    getAuthToken
} from '@auth/authHelpers';

import {
    POL_THEME,
    ThemeContext
} from '@constants/theme';

import type {
    ClaimProps
} from './types';

import styles from './styles';

const Claim: FC<ClaimProps> = (props) => {
    const {
        handleClose,
        isExternallyTriggered,
        organizationDetails
    } = props;

    const theme = useTheme();

    const {
        getAccessTokenSilently,
        isAuthenticated,
        user
    } = useAuth0();

    const themeContext = useContext(ThemeContext);
    const isPOL = themeContext === POL_THEME;

    const [
        presentingModal,
        setPresentingModal
    ] = useState<'out' | 'in' | 'pending' | null>(null);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(false);

    const handlePresentModal = useCallback(async () => {
        if (!isAuthenticated) {
            return setPresentingModal('out');
        }

        try {
            setIsLoading(true);
            setPresentingModal('in');

            const token = await getAuthToken(getAccessTokenSilently);

            const response = await customerDataRequest<OwnershipRequest[]>(
                'get',
                '/organizations/ownership-requests',
                token
            );

            const doesHavePendingRequest = response.data.data.some((details) => organizationDetails.salesforce_id && details.organization_salesforce_id === organizationDetails.salesforce_id);

            if (doesHavePendingRequest) {
                return setPresentingModal('pending');
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }

        return undefined;
    }, [
        getAccessTokenSilently,
        isAuthenticated,
        organizationDetails
    ]);

    const handleCloseModal = () => {
        setPresentingModal(null);

        if (handleClose) {
            handleClose();
        }
    };

    useEffect(() => {
        if (isExternallyTriggered) {
            handlePresentModal();
        }
    }, [
        handlePresentModal,
        isExternallyTriggered
    ]);

    const handleClaim = async () => {
        if (!user?.email) {
            return;
        }

        setIsLoading(true);

        try {
            const token = await getAuthToken(getAccessTokenSilently);

            await customerDataRequest(
                'post',
                `/organizations/${organizationDetails.salesforce_id}/ownership-request`,
                token,
                {
                    emailAddress: user.email
                }
            );

            toast.success('Your request was successful. Please check your e-mail to continue with your ownership request.');
        } catch (error) {
            toast.error('There was an error attempting to send the ownership request. Please try again in a few minutes');
        } finally {
            setIsLoading(false);

            handleCloseModal();
        }
    };

    if (!organizationDetails) {
        return null;
    }

    const ariaCTA = isPOL
        ? 'Do you work at this church? Please Register as Staff to edit the content on this page.'
        : 'Do you work for this organization? Please Register as Staff to edit the content on this page.';

    return (
        <Section
            aria-label={ariaCTA}
            mb={5}
        >
            <Typography variant={'body1'}>
                {isPOL ? 'Do you work at this church? Please ' : 'Do you work for this organization? Please '}
                <Button
                    onClick={handlePresentModal}
                    sx={styles.claimButton}
                    typographyVariant={'body1'}
                    variant={'text'}
                >
                    <b>{'Register as Staff'}</b>
                </Button>
                {' to edit the content on this page.'}
            </Typography>

            {/* Login Required Modal */}
            <Modal
                handleClose={handleCloseModal}
                isVisible={presentingModal === 'out'}
            >
                <Box sx={styles.modalWrapper}>
                    <Typography variant={'h2'}>
                        {'You must be logged in to register as staff'}
                    </Typography>
                    <Typography
                        sx={{
                            mt: 1
                        }}
                        variant={'body1'}
                    >
                        {
                            'If you do not have an account, you can sign up for free. Click the “Log In” button at the top of the page, then click “Sign up.”'
                        }
                    </Typography>
                    <Button
                        onClick={handleCloseModal}
                        sx={{
                            alignSelf: 'end !important',
                            backgroundColor: theme.colors.plantation,
                            color: theme.colors.white,
                            mt: 3
                        }}
                        typographyVariant={'body1'}
                        variant={'contained'}
                    >
                        {'Close'}
                    </Button>
                </Box>
            </Modal>

            {/* Register as Staff Modal */}
            <Modal
                handleClose={handleCloseModal}
                isVisible={presentingModal === 'in'}
            >
                {isLoading ? (
                    <InlineLoader height={272} />
                ) : (
                    <Box sx={styles.modalWrapper}>
                        <Typography variant={'h2'}>{'Register as staff'}</Typography>
                        <Typography
                            sx={{
                                mt: 1
                            }}
                            variant={'body1'}
                        >
                            {`Would you like to manage the listing information for ${organizationDetails.physical_title}? You will be contacted via email within 48 business hours if you are approved.`}
                        </Typography>
                        <ButtonBlock
                            gap={6}
                            isMobileReversed
                            sx={{
                                alignSelf: 'end',
                                mt: 4
                            }}
                        >
                            <Button
                                onClick={handleCloseModal}
                                sx={{
                                    color: theme.colors.plantation
                                }}
                                typographyVariant={'body1'}
                                variant={'text'}
                            >
                                {'Cancel'}
                            </Button>
                            <Button
                                onClick={handleClaim}
                                sx={{
                                    backgroundColor: theme.colors.plantation,
                                    color: theme.colors.white
                                }}
                                typographyVariant={'body1'}
                                variant={'contained'}
                            >
                                {'Continue'}
                            </Button>
                        </ButtonBlock>
                    </Box>
                )}
            </Modal>

            {/* Pending Request Modal */}
            <Modal
                handleClose={handleCloseModal}
                isVisible={presentingModal === 'pending'}
            >
                <Box sx={styles.modalWrapper}>
                    <Typography variant={'h2'}>{'Existing request in review'}</Typography>
                    <Typography
                        sx={{
                            mt: 1
                        }}
                        variant={'body1'}
                    >
                        {
                            'You have previously requested ownership of this page. An LPI representative is in the process of reviewing your request.'
                        }
                    </Typography>
                    <Button
                        onClick={handleCloseModal}
                        sx={{
                            alignSelf: 'end !important',
                            backgroundColor: theme.colors.plantation,
                            color: theme.colors.white,
                            mt: 3
                        }}
                        typographyVariant={'body1'}
                        variant={'contained'}
                    >
                        {'Close'}
                    </Button>
                </Box>
            </Modal>
        </Section>
    );
};

export default Claim;
